<template>
  <div class="container-fluid py-4">
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div class="bg-primary-light p-3 rounded-2 me-3">
              <vue-feather type="clipboard" stroke="#652aa3" size="22"></vue-feather>
            </div>
            <h2 class="page-title mb-0">Performance Review Details</h2>
          </div>
          <div class="d-none d-md-flex align-items-center">
            <button class="btn btn-outline-dark me-2" @click="$router.back()">
              <i class="bi bi-arrow-left me-1"></i>Back
            </button>
            <button class="btn btn-primary" @click="generateReport">
              <i class="bi bi-download me-1"></i>Report
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="review">
      <!-- Review Header -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-body soft-gradient">
              <div class="row align-items-center">
                <div class="col-md-7">
                  <div class="d-flex align-items-center">
                    <div class="avatar avatar-lg me-3">
                      <span class="avatar-title rounded-circle bg-dark-subtle">
                        {{ getInitials(review.employee?.first_name + ' ' + review.employee?.last_name) }}
                      </span>
                    </div>
                    <div>
                      <h3 class="mb-1">{{ review.employee?.first_name }} {{ review.employee?.middle_name }} {{ review.employee?.last_name }}</h3>
                      <div class="text-muted mb-1">
                        <i class="bi bi-briefcase me-1"></i>{{ review.employee?.designation?.name }}
                        <span class="mx-2">•</span>
                        <i class="bi bi-diagram-3 me-1"></i>{{ review.employee?.department?.name }}
                      </div>
                      <div class="d-flex flex-wrap gap-2 mt-2">
                        <span class="badge bg-white text-dark border">
                          <i class="bi bi-arrow-repeat me-1"></i>{{ review.review_cycle?.name }}
                        </span>
                        <span class="badge bg-white text-dark border">
                          <i class="bi bi-person me-1"></i>{{ review.reviewer?.first_name }} {{ review.reviewer?.last_name }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-5 text-md-end">
                  <div class="mb-2">
                    <span :class="getStatusBadgeClass(review.status)" class="me-2 p-2">
                      {{ formatStatus(review.status) }}
                    </span>
                    <span v-if="review.rating" :class="getRatingBadgeClass(review.rating)" class="me-2 p-2">
                      {{ formatRating(review.rating) }}
                    </span>
                  </div>
                  <div v-if="review.overall_score !== null">
                    <div class="text-muted small mb-1">Overall Score</div>
                    <div class="progress rounded-pill" style="height: 20px;">
                      <div
                        class="progress-bar"
                        :class="getScoreBarClass(review.overall_score)"
                        :style="`width: ${review.overall_score}%`"
                      >
                        {{ review.overall_score }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <div class="row mb-4">
        <div class="col-12">
          <ul class="nav nav-tabs nav-fill nav-justified w-100">
            <li class="nav-item">
              <button
                class="nav-link w-100 py-3 fw-semibold"
                :class="{ active: activeTab === 'overview' }"
                @click="activeTab = 'overview'"
              >
                Overview
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link w-100 py-3 fw-semibold"
                :class="{ active: activeTab === 'self-review' }"
                @click="activeTab = 'self-review'"
              >
                Self Review
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link w-100 py-3 fw-semibold"
                :class="{ active: activeTab === 'manager-review' }"
                @click="activeTab = 'manager-review'"
              >
                Manager Review
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link w-100 py-3 fw-semibold"
                :class="{ active: activeTab === 'feedback' }"
                @click="activeTab = 'feedback'"
              >
                360 Feedback
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link w-100 py-3 fw-semibold"
                :class="{ active: activeTab === 'comments' }"
                @click="activeTab = 'comments'"
              >
                Comments
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link w-100 py-3 fw-semibold"
                :class="{ active: activeTab === 'report' }"
                @click="activeTab = 'report'"
              >
                Report
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="row">
        <div class="col-12">
          <!-- Overview Tab -->
          <div v-if="activeTab === 'overview'" class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">Review Overview</h5>

              <div class="row mb-4">
                <div class="col-md-6">
                  <h6 class="text-muted mb-3">Review Information</h6>
                  <div class="row g-3">
                    <div class="col-sm-6">
                      <div class="info-tile p-3 rounded border bg-light h-100">
                        <div class="info-label"><i class="bi bi-person-badge me-2"></i>Reviewer</div>
                        <div class="info-value">{{ review.reviewer?.first_name + ' ' + review.reviewer?.last_name || 'Not assigned' }}</div>
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="info-tile p-3 rounded border bg-light h-100">
                        <div class="info-label"><i class="bi bi-ui-checks-grid me-2"></i>Template</div>
                        <div class="info-value">{{ review.template?.name || 'Default' }}</div>
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="info-tile p-3 rounded border bg-light h-100">
                        <div class="info-label"><i class="bi bi-calendar-plus me-2"></i>Created</div>
                        <div class="info-value">{{ formatDate(review.created_at) }}</div>
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="info-tile p-3 rounded border bg-light h-100">
                        <div class="info-label"><i class="bi bi-clock-history me-2"></i>Last Updated</div>
                        <div class="info-value">{{ formatDate(review.updated_at) }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <h6 class="text-muted mb-3">Summary</h6>
                  <div v-if="review.summary" class="p-3 rounded border bg-light">
                    {{ review.summary }}
                  </div>
                  <p v-else class="text-muted">No summary available yet.</p>
                </div>
              </div>

              <div v-if="review.responses && review.responses.length > 0">
                <h6 class="text-muted mb-3">Category Scores</h6>
                <CategoryScoresChart :scores="categoryScores" />
              </div>
            </div>
          </div>

          <!-- Self Review Tab -->
          <div v-if="activeTab === 'self-review'">
            <SelfReviewResponses v-if="isSelfReviewCompleted" :review="review" />
            <SelfReviewForm
              v-else
              :review="review"
              :canSubmit="canSubmitSelfReview"
              @submitted="handleSelfReviewSubmit"
            />
          </div>

          <!-- Manager Review Tab -->
          <div v-if="activeTab === 'manager-review'">
            <ManagerReviewTab :review="review" />
          </div>

          <!-- 360 Feedback Tab -->
          <div v-if="activeTab === 'feedback'" class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="card-title mb-0">360 Feedback</h5>
                <button
                  v-if="managerReviewSubmitted"
                  @click="showFeedbackModal = true"
                  class="btn btn-primary"
                >
                  <i class="bi bi-plus me-2"></i>Request Feedback
                </button>
              </div>

              <FeedbackRequestsList
                :requests="feedbackRequests"
                :loading="loadingFeedback"
              />
            </div>
          </div>

          <!-- Comments Tab -->
          <div v-if="activeTab === 'comments'" class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">Comments & Discussion</h5>

              <CommentsThread
                :comments="comments"
                :loading="loadingComments"
                @add="handleAddComment"
              />
            </div>
          </div>

          <!-- Report Tab -->
          <div v-if="activeTab === 'report'" class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="card-title mb-0">Performance Report</h5>
                <button @click="generateReport" class="btn btn-primary">
                  <i class="icon-feather-download me-2"></i>Generate Report
                </button>
              </div>

              <PerformanceReport
                v-if="reportData"
                :data="reportData"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 360 Feedback Request Modal -->
    <RequestFeedbackModal
      v-if="showFeedbackModal"
      :review-id="reviewId"
      @close="showFeedbackModal = false"
      @submitted="handleCreateFeedbackRequest"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { usePerformanceStore } from '@/stores/performance';
import moment from 'moment';

import SelfReviewForm from '@/components/performance/SelfReviewForm.vue';
import SelfReviewResponses from '@/components/performance/SelfReviewResponses.vue';
import FeedbackRequestsList from '@/components/performance/FeedbackRequestsList.vue';
import RequestFeedbackModal from '@/components/performance/RequestFeedbackModal.vue';
import CommentsThread from '@/components/performance/CommentsThread.vue';
import PerformanceReport from '@/components/performance/PerformanceReport.vue';
import CategoryScoresChart from '@/components/performance/CategoryScoresChart.vue';
import ManagerReviewTab from '@/components/performance/ManagerReviewTab.vue';

export default {
  name: 'ReviewDetail',
  components: {
    SelfReviewForm,
    SelfReviewResponses,
    FeedbackRequestsList,
    RequestFeedbackModal,
    CommentsThread,
    PerformanceReport,
    CategoryScoresChart,
    ManagerReviewTab
  },
  setup() {
    const route = useRoute();
    const performanceStore = usePerformanceStore();

    const reviewId = computed(() => route.params.id);
    const activeTab = ref('overview');
    const showFeedbackModal = ref(false);

    const loadingFeedback = ref(false);
    const loadingComments = ref(false);

    // Computed properties
    const loading = computed(() => performanceStore.loading);
    const review = computed(() => performanceStore.currentReview);
    const feedbackRequests = computed(() => performanceStore.feedbackRequests);

    const comments = computed(() => performanceStore.reviewComments);
    const reportData = computed(() => performanceStore.reportData);

    const canSubmitSelfReview = computed(() => performanceStore.canSubmitSelfReview);
    const canSubmitManagerReview = computed(() => performanceStore.canSubmitManagerReview);

    // Show Request Feedback after manager review has been submitted
    const managerReviewSubmitted = computed(() => ['calibration', 'completed'].includes(performanceStore.currentReview?.status));

    const isSelfReviewCompleted = computed(() => {
      const r = performanceStore.currentReview;
      if (!r) return false;
      return ['manager_review', 'calibration', 'completed'].includes(r.status) || r.self_reviewed_at !== null;
    });


    const categoryScores = computed(() => {
      if (!review.value?.responses) return {};

      const scores = {};
      review.value.responses.forEach(response => {
        const category = response.criteria?.category?.name;
        if (category) {
          if (!scores[category]) {
            scores[category] = { total: 0, count: 0 };
          }
          scores[category].total += response.score || 0;
          scores[category].count++;
        }
      });

      Object.keys(scores).forEach(category => {
        scores[category] = Math.round(scores[category].total / scores[category].count);
      });

      return scores;
    });

    const categorizedManagerResponses = computed(() => {
      if (!review.value?.responses) return [];
      const categories = {};
      review.value.responses.forEach(response => {
        const categoryName = response.criteria?.category?.name || 'Uncategorized';
        if (!categories[categoryName]) {
          categories[categoryName] = { name: categoryName, responses: [] };
        }
        categories[categoryName].responses.push(response);
      });
      return Object.values(categories);
    });

    // Methods
    const fetchReviewData = async () => {
      await performanceStore.fetchReview(reviewId.value);

      // Load related data based on active tab
      if (activeTab.value === 'feedback') {
        await fetchFeedbackRequests();
      } else if (activeTab.value === 'comments') {
        await fetchComments();
      }
    };

    const fetchFeedbackRequests = async () => {
      loadingFeedback.value = true;
      try {
        await performanceStore.fetchFeedbackRequests(reviewId.value);
      } finally {
        loadingFeedback.value = false;
      }
    };



    const fetchComments = async () => {
      loadingComments.value = true;
      try {
        await performanceStore.fetchComments(reviewId.value);
      } finally {
        loadingComments.value = false;
      }
    };

    const generateReport = async () => {
      await performanceStore.generateReport(reviewId.value);
    };

    const handleSelfReviewSubmit = async (data) => {
      await performanceStore.submitSelfReview(reviewId.value, data);
      await fetchReviewData();
    };

    const handleManagerReviewSubmit = async (data) => {
      await performanceStore.submitManagerReview(reviewId.value, data);
      await fetchReviewData();
    };

    const handleCreateFeedbackRequest = async ({ requested_from, relationship }) => {
      await performanceStore.createFeedbackRequest(reviewId.value, { requested_from, relationship });
      showFeedbackModal.value = false;
      await fetchFeedbackRequests();
    };



    const handleAddComment = async (data) => {
      await performanceStore.addComment(reviewId.value, data);
      await fetchComments();
    };

    // Utility methods
    const getInitials = (name) => {
      if (!name) return '';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    const formatDate = (date) => {
      return moment(date).format('MMM DD, YYYY hh:mm A');
    };

    const formatStatus = (status) => {
      const statusMap = {
        pending: 'Pending',
        self_review: 'Self Review',
        manager_review: 'Manager Review',
        calibration: 'Calibration',
        completed: 'Completed'
      };
      return statusMap[status] || status;
    };

    const formatRating = (rating) => {
      const ratingMap = {
        exceptional: 'Exceptional',
        exceeds: 'Exceeds Expectations',
        meets: 'Meets Expectations',
        needs_improvement: 'Needs Improvement',
        unsatisfactory: 'Unsatisfactory'
      };
      return ratingMap[rating] || rating;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        pending: 'badge bg-secondary',
        self_review: 'badge bg-warning',
        manager_review: 'badge bg-info',
        calibration: 'badge bg-primary',
        completed: 'badge bg-success'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const getRatingBadgeClass = (rating) => {
      const classes = {
        exceptional: 'badge bg-success',
        exceeds: 'badge bg-primary',
        meets: 'badge bg-info',
        needs_improvement: 'badge bg-warning',
        unsatisfactory: 'badge bg-danger'
      };
      return classes[rating] || 'badge bg-secondary';
    };

    const getScoreBarClass = (score) => {
      if (score >= 90) return 'bg-success';
      if (score >= 75) return 'bg-primary';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    // Watch for tab changes
    watch(activeTab, async (newTab) => {
      if (newTab === 'feedback' && feedbackRequests.value.length === 0) {
        await fetchFeedbackRequests();
      } else if (newTab === 'comments' && comments.value.length === 0) {
        await fetchComments();
      } else if (newTab === 'report' && !reportData.value) {
        await generateReport();
      }
    });

    onMounted(() => {
      fetchReviewData();
    });

    return {
      reviewId,
      activeTab,
      showFeedbackModal,
      loadingFeedback,
      loadingComments,
      loading,
      review,
      feedbackRequests,
      comments,
      reportData,
      canSubmitSelfReview,
      canSubmitManagerReview,
      isSelfReviewCompleted,
      managerReviewSubmitted,
      categoryScores,
      categorizedManagerResponses,
      fetchReviewData,
      generateReport,
      handleSelfReviewSubmit,
      handleCreateFeedbackRequest,
      handleAddComment,
      getInitials,
      formatDate,
      formatStatus,
      formatRating,
      getStatusBadgeClass,
      getRatingBadgeClass,
      getScoreBarClass
    };
  }
};
</script>

<style scoped>
.avatar-lg {
  width: 64px;
  height: 64px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 1.5rem;
  font-weight: 600;
}

/* Tabs container styling for better background contrast */
.nav-tabs {
  border-bottom: 0;
  background: #f3f6fb;
  padding: 0.25rem;
  border-radius: 0.5rem;
}

.nav-tabs .nav-link {
  color: #0255a5;
  border: none;
  border-bottom: 0;
  padding: 1rem 1.25rem;
  font-weight: 600;
  border-radius: 0.375rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-tabs .nav-link:hover {
  color: #113c61;
}

.nav-tabs .nav-link.active {
  color: #0255a5;
  background-color: rgba(2, 85, 165, 0.10);
  box-shadow: inset 0 -3px 0 #0255a5;
}

/* Improve hover visibility for non-active tabs */
.nav-tabs .nav-link:not(.active):hover {
  background-color: rgba(2, 85, 165, 0.06);
}

/* Modern header/banner helpers */
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.08);
}
.soft-gradient {
  background: linear-gradient(135deg, rgba(101,42,163,0.06) 0%, rgba(13,110,253,0.04) 100%);
  border-radius: 0.5rem;
}
.info-tile .info-label {
  font-size: 0.775rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6c757d;
}
.info-tile .info-value {
  font-weight: 600;
  color: #212529;
}
</style>
